/**
 * Lucy Live – AudioWorklet + WebSocket Service (Etappe 2)
 * Bidirektionale Sprachinteraktion mit Gemini Multimodal Live API.
 * Master-API-Key bleibt serverseitig (createLiveSessionToken).
 */

import { getApp, type FirebaseApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { buildSalonLiveContext, normalizeScreenContext, resolveLiveUserName } from './bettyLiveContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LiveAudioState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'speaking_user'
  | 'speaking_lucy'
  | 'error';

export type LiveVolumeLevels = {
  /** Mikrofon-Pegel 0..1 */
  user: number;
  /** Lucy-Wiedergabe-Pegel 0..1 */
  lucy: number;
};

export type LiveStateListener = (state: LiveAudioState, detail?: string) => void;
export type LiveVolumeListener = (levels: LiveVolumeLevels) => void;

/** User-Spracherkennung aus Betty Live (inputAudioTranscription). */
export type LiveUserTranscriptPayload = {
  text: string;
  /** true = Turn abgeschlossen (Sprechpause), false = Zwischenstand */
  final: boolean;
};
export type LiveUserTranscriptListener = (payload: LiveUserTranscriptPayload) => void;

export type LiveAudioServiceOptions = {
  /** Firebase App (Default: getApp()) */
  app?: FirebaseApp;
  /** Live-Modell (muss zu createLiveSessionToken passen) */
  model?: string;
  /** Optional: Token selbst holen (Tests / PEBase-CDN-Bridge) */
  fetchToken?: () => Promise<{ token: string; expiresAt: string }>;
  /** Schwellwert für Barge-in / speaking_user (RMS 0..1) */
  bargeInThreshold?: number;
};

export type StartLiveSessionOptions = {
  /** Aktueller PEOne-Bereich (Label), analog Frag Betty `route` */
  currentScreenContext?: string;
  /**
   * conversation = normales Betty-Gespräch (Default)
   * dictation = nur User-Transkript (für Coach-Akte); Betty-Audio wird lokal stummgeschaltet
   */
  mode?: 'conversation' | 'dictation';
  /** Läuft bereits eine Session: hart neu starten (z. B. Wechsel Gespräch → Diktat) */
  force?: boolean;
};

type TokenResponse = {
  token: string;
  expiresAt: string;
};

type CreateLiveSessionTokenRequest = {
  salonContext?: string;
  currentScreenContext?: string;
  userName?: string;
};

type InlineAudioPart = {
  inlineData?: {
    mimeType?: string;
    data?: string;
  };
};

type ServerContentMessage = {
  serverContent?: {
    interrupted?: boolean;
    turnComplete?: boolean;
    modelTurn?: {
      parts?: InlineAudioPart[];
    };
    /** Live-Transkript von Bettys gesprochener Antwort (für Betty-Radar-Erkennung, siehe finalizeTurnForRadar). */
    outputTranscription?: { text?: string };
    /** Live-Transkript der Mandanten-Sprache (für Betty-Radar-Kontext). */
    inputTranscription?: { text?: string };
  };
  setupComplete?: unknown;
  error?: { message?: string; code?: number };
  /** Server liefert laufend Resumption-Handles — für nahtlose Reconnects. */
  sessionResumptionUpdate?: { newHandle?: string; resumable?: boolean };
};

type BettyRadarArgs = {
  kategorie?: string;
  frageText?: string;
  kontext?: string;
};

/** Aktiver Wiedergabe-Source inkl. eigenem GainNode für Fade-out (siehe stopPlayback). */
type PlaybackSourceEntry = {
  source: AudioBufferSourceNode;
  gain: GainNode;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_MODEL = 'models/gemini-3.8-live';
const INPUT_RATE = 16000;
const OUTPUT_RATE = 24000;
const FUNCTIONS_REGION = 'europe-west1';
const KEEP_ALIVE_INTERVAL_MS = 1000;
const KEEP_ALIVE_IDLE_MS = 750;
const SILENCE_FRAME_SAMPLES = 320; // 20 ms bei 16 kHz
const MAX_RECONNECT_ATTEMPTS = 3;
/**
 * Obergrenze für vorgeplanten Wiedergabe-Rückstand. War vorher 4s — bei LÄNGEREN
 * Betty-Antworten (viele Sekunden Audio) kann Gemini Chunks in Bursts liefern, die
 * kurzfristig schneller eintreffen als sie abgespielt werden. Wurde die alte, knappe
 * Grenze überschritten, wurden neu eintreffende Chunks stillschweigend VERWORFEN
 * ("playback backlog capped") → genau das vom Nutzer beschriebene "Wörter verschlucken"
 * bei längeren Sätzen. 24kHz-Mono-PCM ist winzig (48 KB/s), daher ist eine großzügige
 * Grenze hier praktisch kostenlos und verhindert das Droppen legitimer Audio-Chunks.
 */
const MAX_PLAYBACK_BUFFER_SECONDS = 45;
const MAX_ACTIVE_PLAYBACK_SOURCES = 1200;
/**
 * Das AudioWorklet liefert PCM in winzigen Render-Quantum-Häppchen (alle ~2,7 ms).
 * Würde jedes Häppchen einzeln als WS-Nachricht verschickt, wären das >350
 * JSON/Base64-Pakete pro Sekunde – das führt unter realen Netzwerk-/CPU-
 * Bedingungen zu Rucklern, Drops und verstümmeltem Audio (→ Fehlverständnis
 * bei Gemini). Deshalb werden Samples auf ~40 ms-Pakete gebündelt
 * (Google Live Best Practice: 20–40 ms).
 */
const SEND_CHUNK_SAMPLES = 640; // 40 ms bei 16 kHz
/**
 * Ohne Headset koppelt der Lautsprecher-Ausgang akustisch in das Mikrofon zurück
 * (Echo). Eine kurze Echo-Spitze darf NICHT sofort die laufende Betty-Wiedergabe
 * abwürgen — erst eine SUSTAINED Lautstärke gilt als echtes Nutzer-Barge-in.
 */
const BARGE_IN_DEBOUNCE_MS = 450;
/** Mobil/Lautsprecher: längere Sustained-Zeit, sonst Echo → Selbstunterbrechung. */
const BARGE_IN_DEBOUNCE_MOBILE_MS = 700;
/** Während Betty spricht: Schwelle erhöhen (Echo am Handy ist laut). */
const BETTY_PLAYING_THRESHOLD_MULT = 2.2;
const BETTY_PLAYING_THRESHOLD_MOBILE_MULT = 3.0;
/** Sanfter Fade-out (statt hartem stop()) verhindert Knack-/Rattergeräusche. */
const PLAYBACK_FADE_OUT_SECONDS = 0.03;

function isLikelyMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
}

/** Ephemeral Tokens → Constrained-Endpoint (offiziell) */
const WS_CONSTRAINED =
  'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained';

/**
 * Inline AudioWorklet: Float32 → 16 kHz Int16 PCM (Mono), plus RMS-Volume.
 * Registrierung via Blob-URL (kein externes Worklet-File).
 */
const CAPTURE_WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = (options && options.processorOptions) || {};
    this.targetRate = opts.targetRate || 16000;
    this._ratio = sampleRate / this.targetRate;
    this._frac = 0;
    this._muted = false;
    this.port.onmessage = (ev) => {
      if (ev.data && typeof ev.data.muted === 'boolean') this._muted = ev.data.muted;
    };
  }

  process(inputs) {
    const input = inputs[0] && inputs[0][0];
    if (!input || input.length === 0) {
      this.port.postMessage({ type: 'volume', rms: 0 });
      return true;
    }

    let sumSq = 0;
    for (let i = 0; i < input.length; i++) sumSq += input[i] * input[i];
    const rms = Math.sqrt(sumSq / input.length);
    this.port.postMessage({ type: 'volume', rms: Math.min(1, rms * 3.5) });

    if (this._muted) return true;

    const outLen = Math.floor(input.length / this._ratio);
    if (outLen <= 0) return true;
    const pcm = new Int16Array(outLen);
    let outIdx = 0;
    let pos = this._frac;
    while (outIdx < outLen) {
      const i0 = Math.floor(pos);
      const i1 = Math.min(i0 + 1, input.length - 1);
      const t = pos - i0;
      const sample = input[i0] * (1 - t) + input[i1] * t;
      const clamped = Math.max(-1, Math.min(1, sample));
      pcm[outIdx++] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      pos += this._ratio;
    }
    this._frac = pos - input.length;
    if (this._frac < 0) this._frac = 0;

    this.port.postMessage({ type: 'pcm', buffer: pcm.buffer }, [pcm.buffer]);
    return true;
  }
}
registerProcessor('pcm-capture-processor', PcmCaptureProcessor);
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function int16PcmToFloat32(pcm: Int16Array): Float32Array {
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    out[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
  }
  return out;
}

function rmsFloat32(samples: Float32Array): number {
  if (!samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.min(1, Math.sqrt(sum / samples.length) * 3.5);
}

/**
 * Erkennt den Zero-Speculation-Verweigerungstext im Betty-Live-Transkript anhand
 * zweier distinkter Wortgruppen aus BETTY_REFUSAL_TEXT (siehe bettyDirectives.js).
 * Bewusst tolerant gegenüber kleinen STT/TTS-Abweichungen statt exaktem String-Vergleich.
 */
function isZeroSpeculationRefusal(text: string): boolean {
  const t = String(text || '').toLowerCase();
  return t.includes('individuelle frage') && t.includes('nicht beantworten');
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class LiveAudioService {
  private readonly model: string;
  private readonly bargeInThreshold: number;
  private readonly app: FirebaseApp | null;
  private readonly fetchTokenOverride?: () => Promise<TokenResponse>;

  private state: LiveAudioState = 'idle';
  private stateListeners = new Set<LiveStateListener>();
  private volumeListeners = new Set<LiveVolumeListener>();

  private ws: WebSocket | null = null;
  private mediaStream: MediaStream | null = null;
  private captureCtx: AudioContext | null = null;
  private playbackCtx: AudioContext | null = null;
  private captureNode: AudioWorkletNode | null = null;
  private captureSource: MediaStreamAudioSourceNode | null = null;
  private workletUrl: string | null = null;

  private muted = false;
  private setupComplete = false;
  private nextPlayTime = 0;
  private activeSources: PlaybackSourceEntry[] = [];
  private volumeUser = 0;
  private volumeLucy = 0;
  private volumeTimer: number | null = null;
  private keepAliveTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private lastAudioSentAt = 0;
  private manualStop = true;
  /** Screen-Label für Token-Call (Reconnect nutzt denselben Wert). */
  private sessionScreenContext = 'Standard Dashboard';
  private readonly silenceFrame = new Int16Array(SILENCE_FRAME_SAMPLES).buffer as ArrayBuffer;
  private speakingLucyUntil = 0;
  private lastError: string | null = null;

  // Bündelt kleine Worklet-Häppchen zu ~40 ms-WS-Paketen (siehe SEND_CHUNK_SAMPLES).
  private pcmSendQueue: Int16Array[] = [];
  private pcmSendQueueSamples = 0;

  // Entprellung für lokales Barge-in (siehe BARGE_IN_DEBOUNCE_MS).
  private userSpeechAboveThresholdSince: number | null = null;
  private isMobile = isLikelyMobileDevice();
  private onVisibility: (() => void) | null = null;
  private wakeLock: WakeLockSentinel | null = null;
  private wakeLockWanted = false;
  // Mikro-Warmup: true, sobald der Worklet echte PCM-Frames liefert (siehe waitForMicLive).
  private micLive = false;
  private micLiveResolvers: Array<() => void> = [];
  private allowBargeInUplink = false;
  // Session-Resumption: Handle vom Server, um bei Reconnects das GESPRÄCH fortzusetzen
  // statt eine neue Session zu starten (sonst: erneute Begrüßung, Stimmwechsel).
  private resumptionHandle: string | null = null;

  // Betty-Radar: Laufende Transkript-Puffer für die aktuelle Gesprächsrunde (siehe finalizeTurnForRadar).
  private pendingUserTranscript = '';
  private currentBettyTranscript = '';
  private userTranscriptListeners = new Set<LiveUserTranscriptListener>();
  /** Coach-Akte-Diktat: Transkripte ja, Betty-Wiedergabe nein */
  private dictationMode = false;

  constructor(options: LiveAudioServiceOptions = {}) {
    this.model = options.model || DEFAULT_MODEL;
    this.bargeInThreshold = options.bargeInThreshold ?? 0.08;
    this.fetchTokenOverride = options.fetchToken;
    try {
      this.app = options.app ?? getApp();
    } catch {
      this.app = options.app ?? null;
    }
  }

  // ---- Public API ---------------------------------------------------------

  onStateChange(listener: LiveStateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state, this.lastError ?? undefined);
    return () => this.stateListeners.delete(listener);
  }

  onVolumeChange(listener: LiveVolumeListener): () => void {
    this.volumeListeners.add(listener);
    listener({ user: this.volumeUser, lucy: this.volumeLucy });
    return () => this.volumeListeners.delete(listener);
  }

  onUserTranscript(listener: LiveUserTranscriptListener): () => void {
    this.userTranscriptListeners.add(listener);
    return () => this.userTranscriptListeners.delete(listener);
  }

  isDictationMode(): boolean {
    return this.dictationMode;
  }

  getState(): LiveAudioState {
    return this.state;
  }

  async startSession(options?: StartLiveSessionOptions): Promise<void> {
    const nextMode = options?.mode === 'dictation' ? 'dictation' : 'conversation';
    const busy =
      this.state === 'connecting' ||
      this.state === 'connected' ||
      this.state === 'speaking_user' ||
      this.state === 'speaking_lucy';

    if (busy) {
      const modeChanged = this.dictationMode !== (nextMode === 'dictation');
      if (options?.force || modeChanged) {
        this.stopSession(false);
      } else {
        return;
      }
    }

    this.manualStop = false;
    this.reconnectAttempts = 0;
    this.dictationMode = nextMode === 'dictation';
    this.sessionScreenContext = normalizeScreenContext(
      options?.currentScreenContext ||
        (this.dictationMode ? 'Coach-Akte Diktat' : undefined)
    );
    this.setState('connecting');
    this.lastError = null;
    void this.acquireScreenWakeLock();

    try {
      // WICHTIG (Mobil-Stabilität): ERST das Mikrofon komplett hochfahren, DANN die
      // Live-Verbindung öffnen. Beim ersten Start nach App-Neustart ist die Audio-
      // Hardware kalt (getUserMedia + AudioContext + Worklet dauern und ruckeln) —
      // lief die WebSocket-Session dabei schon, riss sie ab und der Reconnect
      // startete eine NEUE Session (erneute Begrüßung, Stimmwechsel, "kommt nicht
      // in Fahrt"). Token wird parallel zum Mikro-Warmup geholt (spart Latenz).
      this.setState('connecting', 'Mikrofon startet...');
      const [{ token }] = await Promise.all([
        this.requestSessionToken(),
        this.startMicrophonePipeline(),
      ]);
      if (!token) throw new Error('Leeres Live-Session-Token.');
      if (this.manualStop) return;

      // Wiedergabe-Kontext JETZT anlegen — noch nah an der Nutzer-Geste (Tipp auf
      // "Sprechen"). Wird er erst beim ersten Audio-Chunk (Sekunden später, ohne
      // Geste) erzeugt, kann Android ihn stumm-gesperrt ("suspended") starten.
      // Hinweis: Die Vanilla-Engine (live-audio-service.js) trägt zusätzlich eine
      // temporäre Ferndiagnose (diagLog/flushDiagnostics → logBettyLiveDiag).
      this.ensurePlaybackCtx();

      // Auf echte Mikro-Frames warten (max. 4 s), damit "verbunden" wirklich
      // "bereit zum Sprechen" bedeutet — der Nutzer soll erst dann fragen.
      await this.waitForMicLive(4000);
      if (this.manualStop) return;

      this.setState('connecting', 'Verbindet...');
      await this.openWebSocket(token);
      this.startKeepAlive();
      this.startVolumeTicker();
      if (this.state === 'connecting') this.setState('connected');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.lastError = msg;
      this.setState('error', msg);
      this.stopSession(false);
      throw err instanceof Error ? err : new Error(msg);
    }
  }

  stopSession(resetState = true): void {
    this.manualStop = true;
    this.releaseScreenWakeLock();
    this.stopReconnect();
    this.stopKeepAlive();
    this.unbindAudioLifecycle();
    this.teardownSocket();
    this.teardownCapture();
    this.teardownPlayback();
    this.stopVolumeTicker();
    this.setupComplete = false;
    this.volumeUser = 0;
    this.volumeLucy = 0;
    this.pendingUserTranscript = '';
    this.currentBettyTranscript = '';
    this.userSpeechAboveThresholdSince = null;
    this.resumptionHandle = null;
    this.allowBargeInUplink = false;
    this.dictationMode = false;
    this.emitVolume();
    if (resetState && this.state !== 'idle') this.setState('idle');
  }

  /**
   * Wartet, bis der Capture-Worklet die ersten ECHTEN PCM-Frames liefert (Android:
   * Hardware-Warmup kann spürbar dauern). Timeout als Sicherheitsnetz, damit die
   * Session nie an einem stummen Mikro hängen bleibt.
   */
  private waitForMicLive(timeoutMs = 4000): Promise<boolean> {
    if (this.micLive) return Promise.resolve(true);
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => resolve(false), timeoutMs);
      this.micLiveResolvers.push(() => {
        window.clearTimeout(timer);
        resolve(true);
      });
    });
  }

  mute(isMuted: boolean): void {
    this.muted = !!isMuted;
    this.captureNode?.port.postMessage({ muted: this.muted });
    if (this.muted) {
      // Beim Stummschalten keine veraltete Restaudio-Restmenge später verzögert nachsenden.
      this.pcmSendQueue = [];
      this.pcmSendQueueSamples = 0;
      this.userSpeechAboveThresholdSince = null;
    }
    if (this.mediaStream) {
      for (const track of this.mediaStream.getAudioTracks()) {
        track.enabled = !this.muted;
      }
    }
  }

  // ---- Token --------------------------------------------------------------

  private async requestSessionToken(): Promise<TokenResponse> {
    if (this.fetchTokenOverride) {
      return this.fetchTokenOverride();
    }
    if (!this.app) {
      throw new Error('Firebase App nicht initialisiert. Option app oder fetchToken setzen.');
    }
    const functions = getFunctions(this.app, FUNCTIONS_REGION);
    const callable = httpsCallable<CreateLiveSessionTokenRequest, TokenResponse>(
      functions,
      'createLiveSessionToken'
    );
    const salonContext = buildSalonLiveContext();
    const currentScreenContext = normalizeScreenContext(this.sessionScreenContext);
    const userName = resolveLiveUserName();
    console.log('================ [BETTY LIVE CONTEXT CHECK] ================');
    console.log('SCREEN:', currentScreenContext);
    console.log('USER:', userName);
    console.log('SALON-CONTEXT LÄNGE:', salonContext?.length || 0);
    console.log('SALON-CONTEXT INHALT:\n', salonContext);
    console.log('============================================================');
    const result = await callable({
      userName,
      salonContext,
      currentScreenContext,
    });
    const data = result.data;
    if (!data?.token) throw new Error('createLiveSessionToken lieferte kein token.');
    return { token: String(data.token), expiresAt: String(data.expiresAt || '') };
  }

  // ---- WebSocket ----------------------------------------------------------

  private openWebSocket(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const constrainedUrl = `${WS_CONSTRAINED}?access_token=${encodeURIComponent(token)}`;
      let settled = false;
      const socket = new WebSocket(constrainedUrl);
      this.ws = socket;

      const fail = (reason: string): void => {
        if (!settled) {
          settled = true;
          if (this.ws === socket) this.teardownSocket();
          reject(new Error(reason));
          return;
        }
        this.handleUnexpectedDisconnect(reason);
      };

      socket.onopen = () => {
        const modelPath = this.model.startsWith('models/') ? this.model : `models/${this.model}`;
        const setup: Record<string, unknown> = {
          model: modelPath,
          generationConfig: {
            responseModalities: ['AUDIO'],
          },
          // Für Betty-Radar-Transkript-Erkennung (siehe finalizeTurnForRadar). Das eigentlich
          // maßgebliche Setup kommt zwar bereits fest verdrahtet aus dem Ephemeral-Token
          // (createLiveSessionToken), diese Felder werden defensiv gespiegelt.
          outputAudioTranscription: {},
          inputAudioTranscription: {},
        };
        // Reconnect: Gespräch FORTSETZEN statt neu beginnen (sonst grüßt Betty erneut
        // und die Stimme kann wechseln). Handle kommt via sessionResumptionUpdate.
        if (this.resumptionHandle) {
          setup.sessionResumption = { handle: this.resumptionHandle };
        }
        socket.send(JSON.stringify({ setup }));
      };

      socket.onmessage = (event) => {
        void this.handleSocketMessage(
          event.data,
          () => {
            if (!settled) {
              settled = true;
              resolve();
            }
          },
          fail
        );
      };

      socket.onerror = () => {
        fail('Die Live-Verbindung wurde unterbrochen.');
      };

      socket.onclose = (ev) => {
        if (this.ws === socket) this.ws = null;
        if (!settled) {
          fail(`WebSocket geschlossen (${ev.code}).`);
          return;
        }
        if (this.manualStop) return;
        if (ev.code !== 1000) {
          this.scheduleReconnect(ev.reason || `Verbindung unterbrochen (${ev.code}).`);
        } else if (this.state !== 'idle' && this.state !== 'error') {
          const detail = 'Die Live-Verbindung wurde beendet.';
          this.lastError = detail;
          this.setState('error', detail);
        }
      };
    });
  }

  private handleUnexpectedDisconnect(detail: string): void {
    if (this.manualStop) return;
    this.teardownSocket();
    this.scheduleReconnect(detail);
  }

  private scheduleReconnect(reason: string): void {
    if (this.manualStop || this.reconnectTimer != null) return;

    this.setupComplete = false;
    this.stopKeepAlive();
    this.stopPlayback(true);

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      const detail = `Verbindung konnte nicht wiederhergestellt werden: ${reason}`;
      this.lastError = detail;
      this.setState('error', detail);
      return;
    }

    this.reconnectAttempts += 1;
    this.lastError = 'Verbindung wird wiederhergestellt...';
    this.setState('connecting', this.lastError);
    const delay = Math.min(3000, 500 * 2 ** (this.reconnectAttempts - 1));

    this.reconnectTimer = window.setTimeout(async () => {
      this.reconnectTimer = null;
      if (this.manualStop) return;
      try {
        const { token } = await this.requestSessionToken();
        if (!token) throw new Error('Leeres Live-Session-Token.');
        await this.openWebSocket(token);
        if (this.manualStop) return;
        this.startKeepAlive();
        this.setState('connected');
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        this.scheduleReconnect(detail);
      }
    }, delay);
  }

  private stopReconnect(): void {
    if (this.reconnectTimer != null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
  }

  private async handleSocketMessage(
    raw: string | Blob,
    onReady?: () => void,
    onFailure?: (detail: string) => void
  ): Promise<void> {
    let text: string;
    if (typeof raw === 'string') {
      text = raw;
    } else if (raw instanceof Blob) {
      text = await raw.text();
    } else {
      return;
    }

    let msg: ServerContentMessage;
    try {
      msg = JSON.parse(text) as ServerContentMessage;
    } catch {
      return;
    }

    if (msg.error) {
      const detail = msg.error.message || 'Serverfehler';
      this.lastError = detail;
      onFailure?.(detail);
      return;
    }

    if (msg.setupComplete != null) {
      this.setupComplete = true;
      onReady?.();
      if (this.state === 'connecting') this.setState('connected');
      return;
    }

    // Resumption-Handle laufend aktualisieren — wird beim Reconnect ins Setup gelegt,
    // damit das Gespräch nahtlos weiterläuft (kein erneutes "Hallo", kein Stimmwechsel).
    if (msg.sessionResumptionUpdate) {
      const upd = msg.sessionResumptionUpdate;
      if (upd.resumable && upd.newHandle) {
        this.resumptionHandle = String(upd.newHandle);
      }
      return;
    }

    const sc = msg.serverContent;
    if (!sc) return;

    if (sc.interrupted === true) {
      this.interruptPlayback('server_interrupted');
      return;
    }

    // Betty-Radar: Läuft rein über Transkript-Erkennung, NICHT über Live-Function-Calling.
    // Grund (empirisch bestätigt): Sobald das Live-Modell während eines Sprech-Turns einen
    // Function Call auslöst, beendet die API die Audio-Generierung für diesen Turn sofort und
    // UNWIDERRUFLICH, sobald der Client die toolResponse bestätigt — das führte zu abgebrochenen/
    // stummen Sätzen. Stattdessen werten wir hier die (server-seitig aktivierte) Output-/
    // Input-Transkription aus, exakt wie beim bestehenden Text-Chat-Mechanismus.
    if (sc.inputTranscription?.text) {
      this.pendingUserTranscript += sc.inputTranscription.text;
      this.emitUserTranscript(this.pendingUserTranscript, false);
    }
    if (sc.outputTranscription?.text) {
      this.currentBettyTranscript += sc.outputTranscription.text;
    }

    const parts = sc.modelTurn?.parts;
    if (parts?.length && !this.dictationMode) {
      for (const part of parts) {
        const b64 = part.inlineData?.data;
        const mime = String(part.inlineData?.mimeType || '');
        if (!b64) continue;
        if (mime && !mime.includes('audio') && !mime.includes('pcm')) continue;
        this.enqueuePlaybackPcm(base64ToArrayBuffer(b64));
      }
    }

    if (sc.turnComplete) {
      this.finalizeTurnForRadar();
    }
  }

  /**
   * Prüft nach Abschluss eines Betty-Sprech-Turns, ob sie den Zero-Speculation-
   * Verweigerungstext ausgesprochen hat, und meldet den Fall (still, nur Text) an
   * das Admin-Radar. Läuft komplett asynchron im Hintergrund, ohne das Gespräch
   * oder die Wiedergabe zu beeinflussen.
   */
  private finalizeTurnForRadar(): void {
    const bettyText = this.currentBettyTranscript.trim();
    const userText = this.pendingUserTranscript.trim();
    this.currentBettyTranscript = '';
    this.pendingUserTranscript = '';
    if (userText) {
      this.emitUserTranscript(userText, true);
    }
    if (!bettyText) return;
    if (isZeroSpeculationRefusal(bettyText)) {
      void this.dispatchRadarEntry({
        kategorie: 'UNBEANTWORTET',
        frageText: userText || '(Frage aus Transkript nicht eindeutig erkennbar)',
        kontext: bettyText.slice(0, 300),
      });
    }
  }

  private emitUserTranscript(text: string, final: boolean): void {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    const payload: LiveUserTranscriptPayload = { text: cleaned, final };
    for (const listener of this.userTranscriptListeners) {
      try {
        listener(payload);
      } catch (err) {
        console.warn('[LiveAudioService] transcript listener error', err);
      }
    }
  }

  private async dispatchRadarEntry(args: BettyRadarArgs): Promise<void> {
    try {
      if (!this.app) return;
      const functions = getFunctions(this.app, FUNCTIONS_REGION);
      const callable = httpsCallable(functions, 'logBettyLiveRadar');
      await callable({
        kategorie: String(args.kategorie || 'UNBEANTWORTET'),
        frageText: String(args.frageText || '').slice(0, 2000),
        kontext: String(args.kontext || '').slice(0, 2000),
      });
    } catch (err) {
      console.warn('[LiveAudioService] Betty-Radar-Dispatch fehlgeschlagen', err);
    }
  }

  private sendPcmChunk(pcmBuffer: ArrayBuffer, keepAlive = false): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.setupComplete) return;
    if (this.muted && !keepAlive) return;
    if (this.isUplinkHeld()) return;

    const data = arrayBufferToBase64(pcmBuffer);
    // Gemini 3.8 Live: `audio` ist das aktuelle Feld. `mediaChunks` ist
    // deprecated und darf nicht parallel im selben Protobuf-oneof stehen.
    const mediaChunk = {
      mimeType: 'audio/pcm;rate=16000',
      data,
    };
    const payload = {
      realtimeInput: {
        audio: mediaChunk,
      },
    };
    try {
      this.ws.send(JSON.stringify(payload));
      this.lastAudioSentAt = Date.now();
    } catch (err) {
      console.warn('[LiveAudioService] send failed', err);
    }
  }

  private isUplinkHeld(): boolean {
    if (!this.isMobile) return false;
    if (this.allowBargeInUplink) return false;
    return this.activeSources.length > 0 || performance.now() < this.speakingLucyUntil;
  }

  private discardPendingUplink(): void {
    this.pcmSendQueue = [];
    this.pcmSendQueueSamples = 0;
  }

  /** Effektive RMS-Schwelle: während Betty spielt deutlich höher (Handy-Echo). */
  private effectiveSpeechThreshold(): number {
    const bettyPlaying = this.state === 'speaking_lucy' || this.activeSources.length > 0;
    const base = this.isMobile ? Math.max(this.bargeInThreshold, 0.12) : this.bargeInThreshold;
    if (!bettyPlaying) return base;
    const mult = this.isMobile ? BETTY_PLAYING_THRESHOLD_MOBILE_MULT : BETTY_PLAYING_THRESHOLD_MULT;
    return base * mult;
  }

  private bargeInDebounceMs(): number {
    return this.isMobile ? BARGE_IN_DEBOUNCE_MOBILE_MS : BARGE_IN_DEBOUNCE_MS;
  }

  /**
   * Lokale Pegel-Auswertung: NUR für UI-State und lokales Barge-in (Fade-out der
   * Wiedergabe). Die maßgebliche Sprach-/Turn-Erkennung macht die SERVER-VAD auf dem
   * kontinuierlichen Audio-Stream — Client-gesteuerte Turns (ActivityStart/End) sind
   * VERBOTEN, siehe Vorfall 18.09. (Betty hörte zu, antwortete aber nie).
   */
  private handleLocalVad(rms: number): void {
    const now = performance.now();
    const threshold = this.effectiveSpeechThreshold();
    const bettyPlaying = this.state === 'speaking_lucy' || this.activeSources.length > 0;

    if (!this.muted && rms >= threshold) {
      if (this.userSpeechAboveThresholdSince == null) {
        this.userSpeechAboveThresholdSince = now;
      }
      const sustainedMs = now - this.userSpeechAboveThresholdSince;
      // Nur ECHTE, anhaltende Sprache unterbricht Betty lokal. Kurze Echo-/Klick-
      // Spitzen (Lautsprecher ohne Headset) reißen sonst mitten im Satz die Wiedergabe ab.
      if (sustainedMs >= this.bargeInDebounceMs() && bettyPlaying) {
        if (!this.allowBargeInUplink) this.allowBargeInUplink = true;
        this.interruptPlayback('user_barge_in');
      }
      if (this.state === 'connected' || this.state === 'speaking_lucy') {
        this.setState('speaking_user');
      }
      return;
    }

    this.userSpeechAboveThresholdSince = null;
    if (this.state === 'speaking_user' && rms < threshold * 0.45) {
      if (performance.now() < this.speakingLucyUntil || this.activeSources.length > 0) {
        this.setState('speaking_lucy');
      } else if (this.setupComplete) {
        this.setState('connected');
      }
    }
  }

  private bindAudioLifecycle(): void {
    this.unbindAudioLifecycle();
    this.onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void this.captureCtx?.resume().catch(() => undefined);
      void this.playbackCtx?.resume().catch(() => undefined);
      if (this.wakeLockWanted) void this.acquireScreenWakeLock();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private unbindAudioLifecycle(): void {
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.onVisibility = null;
    }
  }

  private async acquireScreenWakeLock(): Promise<void> {
    this.wakeLockWanted = true;
    if (this.manualStop) return;
    const wakeLock = navigator.wakeLock;
    if (!wakeLock?.request) return;
    if (document.visibilityState !== 'visible') return;
    try {
      if (this.wakeLock && this.wakeLock.released === false) return;
      const sentinel = await wakeLock.request('screen');
      this.wakeLock = sentinel;
      sentinel.addEventListener('release', () => {
        this.wakeLock = null;
        if (this.wakeLockWanted && !this.manualStop && document.visibilityState === 'visible') {
          void this.acquireScreenWakeLock();
        }
      });
    } catch {
      /* Gerät/Browser lehnt Wake-Lock ab — Gespräch läuft trotzdem. */
    }
  }

  private releaseScreenWakeLock(): void {
    this.wakeLockWanted = false;
    const sentinel = this.wakeLock;
    this.wakeLock = null;
    if (sentinel) {
      try {
        const result = sentinel.release();
        void result.catch(() => undefined);
      } catch {
        /* ignore */
      }
    }
  }

  private startKeepAlive(): void {
    this.stopKeepAlive();
    this.lastAudioSentAt = Date.now();
    this.keepAliveTimer = window.setInterval(() => {
      if (
        !this.manualStop &&
        this.setupComplete &&
        Date.now() - this.lastAudioSentAt >= KEEP_ALIVE_IDLE_MS &&
        !this.isUplinkHeld()
      ) {
        this.sendPcmChunk(this.silenceFrame, true);
      }
    }, KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveTimer != null) {
      window.clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  private teardownSocket(): void {
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
        }
      } catch {
        /* ignore */
      }
    }
    this.ws = null;
  }

  // ---- Capture (Mic → Worklet → WS) ---------------------------------------

  private async startMicrophonePipeline(): Promise<void> {
    this.isMobile = isLikelyMobileDevice();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        // sampleRate-Hinweis weglassen: auf iOS/Android oft 48 kHz — Worklet resamplet auf 16 kHz.
        channelCount: 1,
      },
    });
    this.mediaStream = stream;

    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.captureCtx = new Ctx();
    if (this.captureCtx.state === 'suspended') await this.captureCtx.resume();
    this.bindAudioLifecycle();

    const blob = new Blob([CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' });
    this.workletUrl = URL.createObjectURL(blob);
    await this.captureCtx.audioWorklet.addModule(this.workletUrl);

    this.captureSource = this.captureCtx.createMediaStreamSource(stream);
    this.captureNode = new AudioWorkletNode(this.captureCtx, 'pcm-capture-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      processorOptions: { targetRate: INPUT_RATE },
    });

    this.captureNode.port.onmessage = (ev: MessageEvent) => {
      const data = ev.data as { type?: string; buffer?: ArrayBuffer; rms?: number };
      if (data?.type === 'volume' && typeof data.rms === 'number') {
        this.volumeUser = data.rms;
        this.handleLocalVad(data.rms);
      }
      if (data?.type === 'pcm' && data.buffer) {
        if (!this.micLive) {
          // Erste echte Frames → Mikro ist wirklich bereit (siehe waitForMicLive).
          this.micLive = true;
          const resolvers = this.micLiveResolvers;
          this.micLiveResolvers = [];
          for (const resolve of resolvers) resolve();
        }
        if (this.isUplinkHeld()) {
          this.discardPendingUplink();
          return;
        }
        this.enqueuePcmForSend(new Int16Array(data.buffer));
      }
    };

    this.captureNode.port.postMessage({ muted: this.muted });
    this.captureSource.connect(this.captureNode);
  }

  /** Sammelt kleine Worklet-PCM-Häppchen und sendet sie gebündelt (~40 ms) über die WS. */
  private enqueuePcmForSend(chunk: Int16Array): void {
    this.pcmSendQueue.push(chunk);
    this.pcmSendQueueSamples += chunk.length;
    if (this.pcmSendQueueSamples >= SEND_CHUNK_SAMPLES) {
      this.flushPcmSendQueue();
    }
  }

  private flushPcmSendQueue(): void {
    if (this.pcmSendQueueSamples === 0) return;
    const combined = new Int16Array(this.pcmSendQueueSamples);
    let offset = 0;
    for (const part of this.pcmSendQueue) {
      combined.set(part, offset);
      offset += part.length;
    }
    this.pcmSendQueue = [];
    this.pcmSendQueueSamples = 0;
    this.sendPcmChunk(combined.buffer as ArrayBuffer);
  }

  private teardownCapture(): void {
    try {
      this.captureNode?.port.postMessage({ muted: true });
      this.captureNode?.disconnect();
      this.captureSource?.disconnect();
    } catch {
      /* ignore */
    }
    this.captureNode = null;
    this.captureSource = null;
    this.pcmSendQueue = [];
    this.pcmSendQueueSamples = 0;
    this.micLive = false;
    this.micLiveResolvers = [];

    if (this.captureCtx) {
      void this.captureCtx.close().catch(() => undefined);
      this.captureCtx = null;
    }
    if (this.mediaStream) {
      for (const t of this.mediaStream.getTracks()) t.stop();
      this.mediaStream = null;
    }
    if (this.workletUrl) {
      URL.revokeObjectURL(this.workletUrl);
      this.workletUrl = null;
    }
  }

  // ---- Playback (24 kHz PCM → Speaker) ------------------------------------

  private ensurePlaybackCtx(): AudioContext {
    if (!this.playbackCtx || this.playbackCtx.state === 'closed') {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      // sampleRate-Hinweis: Mobile Browser ignorieren 24 kHz oft und nehmen 48 kHz —
      // wir deklarieren Buffer weiterhin mit OUTPUT_RATE, Browser resamplet.
      try {
        this.playbackCtx = new Ctx({ sampleRate: OUTPUT_RATE });
      } catch {
        this.playbackCtx = new Ctx();
      }
      this.nextPlayTime = this.playbackCtx.currentTime;
    }
    if (this.playbackCtx.state === 'suspended') {
      void this.playbackCtx.resume().catch(() => undefined);
    }
    return this.playbackCtx;
  }

  private enqueuePlaybackPcm(pcmBuffer: ArrayBuffer): void {
    const ctx = this.ensurePlaybackCtx();
    if (ctx.state === 'suspended') void ctx.resume();
    if (this.isMobile) this.discardPendingUplink();

    const usableBytes = pcmBuffer.byteLength - (pcmBuffer.byteLength % 2);
    if (usableBytes === 0) return;
    const int16 = new Int16Array(pcmBuffer, 0, usableBytes / 2);
    if (!int16.length) return;

    const now = ctx.currentTime;
    const bufferedSeconds = Math.max(0, this.nextPlayTime - now);
    if (
      bufferedSeconds >= MAX_PLAYBACK_BUFFER_SECONDS ||
      this.activeSources.length >= MAX_ACTIVE_PLAYBACK_SOURCES
    ) {
      console.warn('[LiveAudioService] playback backlog capped', {
        bufferedSeconds: Math.round(bufferedSeconds * 10) / 10,
        sources: this.activeSources.length,
      });
      return;
    }

    const float32 = int16PcmToFloat32(int16);
    this.volumeLucy = Math.max(this.volumeLucy, rmsFloat32(float32));

    // Falls Capture-Rate ≠ 24 kHz: AudioBuffer mit OUTPUT_RATE deklarieren;
    // Browser resampelt bei Bedarf auf die Context-Rate.
    const audioBuffer = ctx.createBuffer(1, float32.length, OUTPUT_RATE);
    audioBuffer.copyToChannel(float32, 0);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    // GainNode zwischen Source und Ausgang, damit interruptPlayback/stopPlayback
    // sanft ausblenden kann statt mit hartem stop() zu knacken/rattern.
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(1, ctx.currentTime);
    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    if (this.nextPlayTime < now) this.nextPlayTime = now;
    const startAt = this.nextPlayTime;
    this.nextPlayTime = startAt + audioBuffer.duration;
    this.speakingLucyUntil = performance.now() + audioBuffer.duration * 1000 + 80;

    const entry: PlaybackSourceEntry = { source, gain: gainNode };
    source.onended = () => {
      this.activeSources = this.activeSources.filter((s) => s !== entry);
      try {
        source.disconnect();
        gainNode.disconnect();
      } catch {
        /* already disconnected */
      }
      if (this.activeSources.length === 0 && this.state === 'speaking_lucy') {
        this.volumeLucy = 0;
        this.allowBargeInUplink = false;
        if (this.setupComplete && this.state !== 'error') this.setState('connected');
      }
    };

    this.activeSources.push(entry);
    source.start(startAt);
    if (this.state === 'connected' || this.state === 'speaking_user') {
      this.setState('speaking_lucy');
    }
  }

  private interruptPlayback(_reason: string): void {
    this.stopPlayback(true);
    this.volumeLucy = 0;
    this.emitVolume();
    if (this.setupComplete && this.state !== 'error' && this.state !== 'idle') {
      this.setState(this.volumeUser >= this.bargeInThreshold ? 'speaking_user' : 'connected');
    }
  }

  private stopPlayback(resetClock: boolean): void {
    const ctx = this.playbackCtx;
    const now = ctx ? ctx.currentTime : 0;
    for (const entry of this.activeSources) {
      const { source, gain } = entry;
      try {
        source.onended = null;
        if (ctx) {
          // Sanfter Fade-out statt hartem stop() → verhindert Knack-/Rattergeräusche
          // beim (ggf. fälschlichen) Barge-in-Abbruch.
          gain.gain.cancelScheduledValues(now);
          gain.gain.setValueAtTime(gain.gain.value, now);
          gain.gain.linearRampToValueAtTime(0, now + PLAYBACK_FADE_OUT_SECONDS);
          source.stop(now + PLAYBACK_FADE_OUT_SECONDS + 0.005);
          const cleanupDelayMs = (PLAYBACK_FADE_OUT_SECONDS + 0.02) * 1000;
          window.setTimeout(() => {
            try {
              source.disconnect();
              gain.disconnect();
            } catch {
              /* already disconnected */
            }
          }, cleanupDelayMs);
        } else {
          source.stop();
          source.disconnect();
          gain.disconnect();
        }
      } catch {
        /* already stopped */
      }
    }
    this.activeSources = [];
    if (resetClock && this.playbackCtx) {
      this.nextPlayTime = this.playbackCtx.currentTime;
    } else {
      this.nextPlayTime = 0;
    }
    this.speakingLucyUntil = 0;
  }

  private teardownPlayback(): void {
    this.stopPlayback(true);
    const ctx = this.playbackCtx;
    this.playbackCtx = null;
    this.nextPlayTime = 0;
    if (ctx && ctx.state !== 'closed') {
      void ctx.close().catch(() => undefined);
    }
  }

  // ---- State / Volume -----------------------------------------------------

  private setState(next: LiveAudioState, detail?: string): void {
    if (this.state === next && detail == null) return;
    this.state = next;
    for (const listener of this.stateListeners) {
      try {
        listener(next, detail);
      } catch (err) {
        console.warn('[LiveAudioService] state listener error', err);
      }
    }
  }

  private emitVolume(): void {
    const levels: LiveVolumeLevels = { user: this.volumeUser, lucy: this.volumeLucy };
    for (const listener of this.volumeListeners) {
      try {
        listener(levels);
      } catch (err) {
        console.warn('[LiveAudioService] volume listener error', err);
      }
    }
  }

  private startVolumeTicker(): void {
    this.stopVolumeTicker();
    this.volumeTimer = window.setInterval(() => {
      // Lucy-Volume nachlassen, wenn nichts spielt
      if (this.activeSources.length === 0) {
        this.volumeLucy *= 0.85;
        if (this.volumeLucy < 0.01) this.volumeLucy = 0;
      }
      this.emitVolume();
    }, 50);
  }

  private stopVolumeTicker(): void {
    if (this.volumeTimer != null) {
      window.clearInterval(this.volumeTimer);
      this.volumeTimer = null;
    }
  }
}

/** Singleton-Helfer für UI-Etappe 3 */
let sharedInstance: LiveAudioService | null = null;

export function getLiveAudioService(options?: LiveAudioServiceOptions): LiveAudioService {
  if (!sharedInstance) sharedInstance = new LiveAudioService(options);
  return sharedInstance;
}

export default LiveAudioService;
